import { FC } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BreadcrumbItem, WebPageSchema } from "./types";
import { createBreadcrumbSchema, createWebPageSchema } from "./schema-utils";
import { BASE_URL, getCanonicalUrl, getLanguageUrls } from "@/config/env";
import {
  formatSeoDescription,
  formatSeoTitle,
  hasOnlyTrackingParams,
} from "./seoMetadata";

export interface PageSeoProps {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noindex?: boolean;
  /**
   * Robots directive override. Prefer this for Sweden-first EN money pages
   * that must stay crawlable (`noindex,follow`) without ranking against SV.
   * When omitted, `noindex` maps to `noindex,nofollow`.
   */
  robots?: "noindex,follow" | "noindex,nofollow";
  canonicalPath?: string; // Optional override for canonical path
  structuredData?: Record<string, unknown>;
  publishedTime?: string;
  modifiedTime?: string;
  breadcrumbs?: BreadcrumbItem[];
  author?: {
    name: string;
    url?: string;
  };
  isSpecialPage?: boolean; // Flag for pages with special handling
  alternateLanguageUrl?: string | null; // Override for alternate language URL
  language?: "sv" | "en"; // Explicit route language for SSR-safe localized metadata
  disableStructuredData?: boolean;
}

export const PageSeo: FC<PageSeoProps> = ({
  title,
  description,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  ogImage,
  ogType = "website",
  noindex = false,
  robots,
  canonicalPath,
  structuredData,
  publishedTime,
  modifiedTime,
  breadcrumbs,
  author,
  isSpecialPage = false,
  alternateLanguageUrl,
  language,
  disableStructuredData = false,
}) => {
  const { t, i18n } = useTranslation(["common", "home"]);
  const location = useLocation();
  const effectiveLanguage = language || (i18n.language === "sv" ? "sv" : "en");

  // Tracking-only query variants are served noindex,follow without hreflang
  // (see hasOnlyTrackingParams in seoMetadata.ts).
  const isTrackingOnlyVariant = hasOnlyTrackingParams(location.search);

  // FIXED: Pages should be indexed by default, only apply noindex if explicitly set to true
  const robotsContent =
    robots ??
    (noindex
      ? "noindex,nofollow"
      : isTrackingOnlyVariant
        ? "noindex,follow"
        : undefined);
  const shouldNoIndex = Boolean(robotsContent);

  // Locale-aware brand-2.0 default share card when a page does not supply its own.
  // og:image and twitter:image must be absolute: share crawlers do not resolve a
  // site-relative path, so a page passing "/images/..." gets no preview card.
  const resolvedOgImage = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${BASE_URL}${ogImage}`
    : `${BASE_URL}/images/og/default${effectiveLanguage === "en" ? "-en" : ""}.jpg`;
  const translatedSiteName = t("home:seo.site", { defaultValue: "Caire" });
  const siteName =
    translatedSiteName === "home:seo.site" ? "Caire" : translatedSiteName;
  const fullTitle = formatSeoTitle(title, siteName, effectiveLanguage);
  const seoDescription = formatSeoDescription(description, effectiveLanguage);
  const resolvedOgTitle = formatSeoTitle(
    ogTitle ?? title,
    siteName,
    effectiveLanguage,
  );
  const resolvedOgDescription = formatSeoDescription(
    ogDescription ?? description,
    effectiveLanguage,
  );
  const resolvedTwitterTitle = formatSeoTitle(
    twitterTitle ?? title,
    siteName,
    effectiveLanguage,
  );
  const resolvedTwitterDescription = formatSeoDescription(
    twitterDescription ?? description,
    effectiveLanguage,
  );

  // Determine the current path to use for canonical and alternate URLs
  const currentPath = canonicalPath || location.pathname;

  // Generate canonical URL using the utility function
  const canonicalUrl = getCanonicalUrl(currentPath, effectiveLanguage);

  // Use provided alternateLanguageUrl if available, otherwise calculate from current path
  const { svUrl, enUrl } = alternateLanguageUrl
    ? {
        svUrl:
          effectiveLanguage === "en"
            ? `${BASE_URL}${alternateLanguageUrl}`
            : canonicalUrl,
        enUrl:
          effectiveLanguage === "sv"
            ? `${BASE_URL}${alternateLanguageUrl}`
            : canonicalUrl,
      }
    : getLanguageUrls(currentPath);

  // Create breadcrumb schema if breadcrumbs are provided
  const breadcrumbsSchema =
    breadcrumbs && !disableStructuredData
      ? createBreadcrumbSchema(
          breadcrumbs.map((item) => ({
            name: item.name,
            url: item.item,
            position: item.position,
          })),
        )
      : null;

  // Create WebPage structured data if not provided
  const webPageSchema =
    !disableStructuredData && !structuredData
      ? createWebPageSchema({
          name: title,
          description: seoDescription,
          url: canonicalUrl,
          datePublished: publishedTime,
          dateModified: modifiedTime || publishedTime,
          isPartOf: {
            "@type": "WebSite",
            name: siteName,
            url: BASE_URL,
          },
        })
      : null;

  // Derive the OG image MIME type from the asset extension instead of always
  // declaring image/jpeg (many pages pass .png/.webp OG cards).
  const ogImageType = resolvedOgImage.endsWith(".png")
    ? "image/png"
    : resolvedOgImage.endsWith(".webp")
      ? "image/webp"
      : "image/jpeg";

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={seoDescription} />
      {robotsContent && <meta name="robots" content={robotsContent} />}

      {/* No per-page AI-crawler meta tags. `<meta name="GPTBot">`,
       * "Google-Extended", "Applebot-Extended" and the rest were never
       * directives any of those vendors read — each is a robots.txt
       * user-agent token, and Google and Apple document theirs as
       * robots.txt-only. public/robots.txt already lets every crawler read
       * the public site through its single `User-agent: *` group, which is
       * the signal that actually grants access.
       *
       * They also never rendered: the block was wrapped in a fragment, and
       * react-helmet-async 2.x drops Fragment children silently. Removed
       * rather than repaired — repairing it would have started emitting
       * eight inert tags on every page for the first time.
       * See the no-Fragments-in-Helmet gate in ssr-seo-head.test.ts. */}

      {/* Open Graph */}
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:alt" content={resolvedOgTitle} />
      <meta property="og:image:type" content={ogImageType} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:locale"
        content={effectiveLanguage === "en" ? "en_US" : "sv_SE"}
      />
      {effectiveLanguage === "en" && (
        <meta property="og:locale:alternate" content="sv_SE" />
      )}
      {effectiveLanguage === "sv" && (
        <meta property="og:locale:alternate" content="en_US" />
      )}

      {/* Article-specific Open Graph */}
      {ogType === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === "article" && author && (
        <meta property="article:author" content={author.name} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTwitterTitle} />
      <meta name="twitter:description" content={resolvedTwitterDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Alternate language versions.
       *
       * Withheld on a noindex page. hreflang is a reciprocal annotation: it
       * tells Google "these URLs are the same document in another language",
       * and a URL that is out of the index cannot be the answer for any
       * locale, so the pair can never be reciprocated. Semrush read the
       * unguarded version as 175 "hreflang conflicts within page source
       * code" across 70 URLs — every tracking-param variant below, each
       * serving `noindex,follow` while naming three alternates that do not
       * include itself.
       *
       * The canonical above stays: it is a same-document statement, not a
       * cross-locale one, and it is what folds the variant back into the
       * clean URL.
       *
       * Guarded per element, NOT wrapped in a fragment. react-helmet-async 2.x
       * does not flatten `<>…</>` — it drops the children silently, with no
       * error and no output. The "AI Agent Directives" block above is wrapped
       * that way and renders on zero pages in production as a result; writing
       * this guard the same way would have deleted hreflang sitewide while
       * every test that reads the rendered head still passed.
       */}
      {!shouldNoIndex && <link rel="alternate" hrefLang="sv" href={svUrl} />}
      {!shouldNoIndex && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {!shouldNoIndex && (
        <link rel="alternate" hrefLang="x-default" href={svUrl} />
      )}

      {/* Structured Data */}
      {!disableStructuredData && structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Add WebPage schema if no custom structured data was provided */}
      {webPageSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            ...webPageSchema,
          })}
        </script>
      )}

      {/* Breadcrumb Schema */}
      {breadcrumbsSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            ...breadcrumbsSchema,
          })}
        </script>
      )}
    </Helmet>
  );
};
